import type Experiments from '@wix/wix-experiments';
import { CONDUCTOR_FT, EXPERIMENTS } from '../stores/constants';
import {
  AuthMethod,
  getLastUsedAuthMethod,
  getLastUsedAuthMethodAgeDays,
} from './lastUsedAuthMethod';

/**
 * Measurement attribution for the "Last used" badge experiment.
 *
 * The three KPIs (time to successful login, login success rate, failure rate)
 * are all a comparison of variant B against variant A, so every event has to
 * say two things: which variant this browser is in, and what the stored hint
 * was. The second part matters most — a visitor with no hint cannot be
 * affected by the badge, and including them buries the effect under people the
 * feature never touches.
 *
 * The variant is therefore read RAW here, not through
 * `shouldShowLastUsedAuthMethod()`. That predicate collapses "control" and
 * "not conducted" into one `false`, and the control group has to stay
 * identifiable or there is nothing to compare against. For the same reason the
 * hint is reported in variant A too, where no badge is drawn: that is exactly
 * the counterfactual population.
 */

export const LAST_USED_BI_PREFIX = 'lastUsed';

export type LastUsedVariant = 'A' | 'B' | 'unset';
export type LastUsedHint = AuthMethod | 'none';

export interface LastUsedAttribution {
  /** Conducted Conductor variant: "B" shows the badge, "A" is the control. */
  variant: LastUsedVariant;
  /** The method the previous successful login used, or "none". */
  hint: LastUsedHint;
  /** Whether the method this event is about is the hinted one. */
  match: boolean;
  /**
   * Whole days since the hint was written, or undefined when there is none.
   * The hint never expires, so this is how the experiment can tell whether an
   * old hint is acted on as readily as a fresh one.
   */
  ageDays?: number;
}

const readVariant = (experiments?: Experiments): LastUsedVariant => {
  const value = experiments?.get(EXPERIMENTS.SHOW_LAST_USED_AUTH_METHOD);
  if (value === CONDUCTOR_FT.ENABLED) {
    return 'B';
  }
  if (value === CONDUCTOR_FT.DISABLED) {
    return 'A';
  }
  return 'unset';
};

export const getLastUsedAttribution = (
  experiments?: Experiments,
  method?: AuthMethod,
): LastUsedAttribution => {
  const hint = getLastUsedAuthMethod();
  return {
    variant: readVariant(experiments),
    hint: hint ?? 'none',
    match: !!method && !!hint && method === hint,
    ageDays: getLastUsedAuthMethodAgeDays(),
  };
};

/**
 * Flat fields for Panorama transactions, which take a data object. Timing is
 * already measured by the login transactions, so the KPI "time to successful
 * login" needs nothing more than these to become sliceable.
 */
export const lastUsedTransactionData = (
  experiments?: Experiments,
  method?: AuthMethod,
) => {
  const { variant, hint, match, ageDays } = getLastUsedAttribution(
    experiments,
    method,
  );
  return {
    lastUsedVariant: variant,
    lastUsedHint: hint,
    lastUsedMatch: match,
    lastUsedAgeDays: ageDays ?? -1,
  };
};

/**
 * The same attribution squeezed into one string, for the BI events that have
 * no room for three fields.
 *
 * It rides the existing `view` field on the hls2 login events. `view` is
 * unused by this app today, so nothing is overwritten, and the `lastUsed:`
 * prefix plus `_artifact_name = com.wixpress.users.login-react-app` makes the
 * rows unambiguous in a table other producers also write to.
 *
 * Shape: `lastUsed:v=B;hint=google;match=1;age=12`
 *
 * `age` is whole days since the hint was written, `-1` when unknown.
 *
 * This encoding exists because a dedicated BI event needs catalog
 * registration and a logger republish, which would block this PR. Replacing
 * it with typed events is tracked as a follow-up.
 */
export const encodeLastUsedAttribution = ({
  variant,
  hint,
  match,
  ageDays,
}: LastUsedAttribution): string =>
  `${LAST_USED_BI_PREFIX}:v=${variant};hint=${hint};match=${
    match ? 1 : 0
  };age=${ageDays ?? -1}`;

/** Convenience: the encoded string for the `view` field, in one call. */
export const lastUsedBiView = (
  experiments?: Experiments,
  method?: AuthMethod,
): string =>
  encodeLastUsedAttribution(getLastUsedAttribution(experiments, method));
